import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import RewardsService from "../../services/reward";
import Utils from "../../utility";
import { useDispatch, useSelector } from "react-redux";
import { hideLoader, removeUser, showLoader } from "../../slices/userSlice";
import {
  successMessageConstant,
  failureMessageConstant,
  keyConstants
} from "../../constants";
import DropDown from "../../common/components/dropdown";
import PropTypes from "prop-types";

const AddPartnerCoupon = ({
  handleClose,
  item,
  isEdit,
  fetchCouponData,
  page,
  itemsPerPage
}) => {
  const {
    COUPON_NAME_REQUIRED,
    COUPON_NAME_TOO_LONG,
    OPTIONS_MAX_LIMIT,
    CATEGORY_REQUIRED,
    EACH_CATEGORY_MUST_BE_UNIQUE
  } = failureMessageConstant;

  const [categories, setCategories] = useState([]);
  const [showOption, setShowOption] = useState(false);
  const [options, setOptions] = useState(
    isEdit ? item?.categories : [{ maxOptions: 1, category: "" }]
  );
  const [optionError, setOptionError] = useState("");
  const [currentDropDownIndex, setCurrentDropDownIndex] = useState(0);
  const maxTotalOptions = 4;
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
    reset
  } = useForm();

  const { IS_REFRESH_TOKEN_LOADING } = keyConstants;

  const addUpdatePartnerCoupon = async (formData) => {
    const tokenStatus = await Utils.checkTokenExpireOrNot();
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) return;
      dispatch(removeUser({}));
      return;
    }

    const hasEmptyCategory = options.some((opt) => !opt.category.name);
    if (hasEmptyCategory) {
      setOptionError(CATEGORY_REQUIRED);
      return;
    }

    // Check for unique category names
    const categoryNames = options.map((opt) => opt.category.name);
    const hasDuplicateCategory =
      new Set(categoryNames).size !== categoryNames.length;

    if (hasDuplicateCategory) {
      setOptionError(EACH_CATEGORY_MUST_BE_UNIQUE);
      return;
    }

    try {
      dispatch(showLoader());
      const rewardsService = new RewardsService();
      const payload = isEdit
        ? {
            partnerCouponId: item?._id,
            couponName: formData.couponName,
            categories: options.map((opt) => ({
              maxOptions: opt.maxOptions,
              category: opt.category.name
            }))
          }
        : {
            businessId: user?._id,
            couponName: formData.couponName,
            categories: options.map((opt) => ({
              maxOptions: opt.maxOptions,
              category: opt.category.name
            }))
          };

      if (isEdit) {
        await rewardsService.updatePartnerCoupon(payload);
      } else {
        await rewardsService.addPartnerCoupon(payload);
      }
      handleClose();
      const successMessage = isEdit
        ? successMessageConstant.PARTNER_COUPON_UPDATED_SUCCESSFULLY
        : successMessageConstant.PARTNER_COUPON_ADDED_SUCCESSFULLY;
      await fetchCouponData((page - 1) * itemsPerPage, itemsPerPage);
      Utils.successToastMessage(successMessage);
    } catch (error) {
      console.error(error);
      if (error?.response?.data?.message)
        Utils.failureToastMessage(
          Utils.capitalizeFirstLetter(error.response.data.message)
        );
      else
        Utils.failureToastMessage(Utils.capitalizeFirstLetter(error?.message));
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleInputChange = (fieldName, e) => {
    let value = e.target.value;
    if (fieldName === "couponName") {
      if (errors.couponName) {
        setError("couponName", { message: "" });
      }
    }
    setValue(fieldName, value);
  };

  const handleAddOption = () => {
    const totalCount = options.reduce((acc, opt) => acc + opt.maxOptions, 0);

    if (totalCount >= maxTotalOptions) {
      setOptionError(OPTIONS_MAX_LIMIT);
      return;
    }

    const newOptions = [...options, { maxOptions: 1, category: "" }]; // Default maxOptions to 1
    setOptions(newOptions);
    if (optionError) {
      setOptionError("");
    }
  };

  const handleOptionChange = (index, field, value) => {
    setOptions((prevOptions) => {
      const updatedOptions = prevOptions.map((opt, i) =>
        i === index
          ? {
              ...opt,
              [field]:
                field === "maxOptions"
                  ? Math.min(
                      maxTotalOptions,
                      Math.max(1, opt.maxOptions + value)
                    )
                  : value
            }
          : opt
      );

      const totalCount = updatedOptions.reduce(
        (acc, opt) => acc + opt.maxOptions,
        0
      );

      if (totalCount > maxTotalOptions) {
        setOptionError(OPTIONS_MAX_LIMIT);
        return prevOptions;
      }

      setOptionError("");
      return updatedOptions;
    });
  };

  const handleDeleteOption = (index) => {
    setOptions((prevOptions) => {
      const updatedOptions = prevOptions.filter((_, i) => i !== index);

      const totalCount = updatedOptions.reduce(
        (acc, opt) => acc + opt.maxOptions,
        0
      );
      if (totalCount < maxTotalOptions) {
        setOptionError("");
      }

      return updatedOptions;
    });
  };

  const fetchCategories = async () => {
    const tokenStatus = await Utils.checkTokenExpireOrNot(fetchCategories);
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) {
        dispatch(showLoader());
        return;
      }
      dispatch(removeUser({}));
      return;
    }
    try {
      dispatch(showLoader());
      const rewardsService = new RewardsService();
      const response = await rewardsService.getCategoriesAndMerchants();
      const keyValueCategories = response.categories.map((item) => ({
        name: item,
        value: item
      }));
      setCategories(keyValueCategories);
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);
  useEffect(() => {
    if (isEdit && item?.name) {
      setValue("couponName", item.name);
    }
  }, [isEdit, item?.name]);
  useEffect(() => {
    if (isEdit && item?.name) {
      setValue("couponName", item.name);
    }
    if (isEdit && item?.categories) {
      const updatedCategories = item.categories.map((item) => {
        return {
          ...item,
          category: {
            name: item.category,
            value: item.category
          }
        };
      });
      setOptions(updatedCategories);
    }
    if (!isEdit) {
      reset();
    }
  }, [isEdit, item, categories, reset, setValue]);

  return (
    <div className="fixed left-0 top-0 z-10 flex size-full items-start justify-center border-grey-DDDFDE bg-black-00000050 px-3">
      <form
        onSubmit={handleSubmit(addUpdatePartnerCoupon)}
        className="relative mt-3 max-h-95vh w-full max-w-547px overflow-y-auto rounded-lg bg-white px-3 pt-23px md:mt-9 md:overflow-y-visible md:px-6"
      >
        <div className="flex flex-col">
          <div className="font-MontserratSemiBold text-ft22-26.82-0.4 text-black-20">
            {isEdit ? "Edit Coupon" : "Add Partner Coupon"}
          </div>
          <div className="mt-4.5 font-MontserratMedium text-ft15-26.25 text-black-30">
            Coupon Name
          </div>
          <input
            type="text"
            className={`h-49px w-full rounded border ${errors.discountValue?.message ? "border-red-10" : "border-grey-D9D9D9"}  px-13px py-10.72px font-MontserratMedium placeholder:text-grey-20 focus:outline-none`}
            placeholder="General"
            {...register("couponName", {
              required: COUPON_NAME_REQUIRED,
              maxLength: {
                value: 20,
                message: COUPON_NAME_TOO_LONG
              }
            })}
            onChange={(e) => handleInputChange("couponName", e)}
          />
          {errors.couponName && (
            <p className="mt-2 font-MontserratRegular text-ft13-15.85-0.26 text-red-10">
              {errors.couponName.message}
            </p>
          )}
          <div>
            {options.map((opt, index) => (
              <div className="mt-5 flex flex-col" key={`option-${index}`}>
                <div className="flex flex-col gap-1 md:flex-row md:gap-17px">
                  <div>
                    <div className="font-MontserratMedium text-ft15-26.25 text-black-30">
                      Max # of options:
                    </div>
                    <div className="flex h-11.5 w-149px justify-between rounded border border-grey-D9D9D9 py-2.5 pl-15px pr-11px">
                      <span>{opt.maxOptions}</span>
                      <div className="flex flex-col justify-between">
                        <img
                          src="/images/arrow-up.svg"
                          alt="Arrow Up"
                          className="cursor-pointer"
                          onClick={() =>
                            handleOptionChange(index, "maxOptions", -1)
                          }
                        />
                        <img
                          src="/images/arrow-down.svg"
                          alt="Arrow Down"
                          className="cursor-pointer"
                          onClick={() =>
                            handleOptionChange(index, "maxOptions", 1)
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 md:gap-17px">
                    <div className="w-full">
                      <div className=" font-MontserratMedium text-ft15-26.25 text-black-30">
                        Category
                      </div>
                      <DropDown
                        index={index}
                        value={opt.category}
                        setValue={(value) =>
                          handleOptionChange(index, "category", value)
                        }
                        data={categories}
                        placeHolder={"Choose Category"}
                        customizeDropDown={{
                          maxWidth: "max-w-full",
                          height: "h-11.5"
                        }}
                        textwidth="w-50 xs:w-full md:w-66"
                        showOption={showOption}
                        setShowOption={setShowOption}
                        blackArrow={true}
                        currentDropDownIndex={currentDropDownIndex}
                        setCurrentDropDownIndex={setCurrentDropDownIndex}
                        isSearchable={true}
                      />
                    </div>
                    {options.length > 1 && (
                      <img
                        src="/images/delete.svg"
                        alt="Delete Icon"
                        onClick={() => handleDeleteOption(index)}
                        className="mr-2 mt-17px cursor-pointer md:mr-0"
                      />
                    )}
                  </div>
                </div>
                <span className="mt-1.5 font-MontserratMedium text-ft12-16 text-grey-767676">
                  {`Up to ${opt.maxOptions} ${opt?.category?.name ? opt?.category?.name : ""} option will be offered`}
                </span>
              </div>
            ))}
          </div>
          <div
            onClick={handleAddOption}
            className="mt-6 flex h-8 w-34 cursor-pointer items-center justify-center rounded border border-grey-D9D9D9 bg-purple-6C36B0 font-MontserratMedium text-ft15-26.25 text-white"
          >
            Add Category
          </div>
          {optionError && (
            <p className="mt-1.5 font-MontserratRegular text-ft12-16 text-red-10">
              {optionError}
            </p>
          )}

          <div className="mb-8 mt-29px flex justify-between gap-2">
            <div>
              <button
                type="button"
                onClick={handleClose}
                className="h-12 w-35 rounded border border-grey-D9D9D9 md:w-62"
              >
                <span className="font-MontserratMedium text-ft15-26.25 text-black-000000">
                  Cancel
                </span>
              </button>
            </div>
            <div>
              <button
                type="submit"
                className="h-12 w-35 rounded border border-grey-D9D9D9 bg-purple-6C36B0 text-white md:w-62"
              >
                <span className="font-MontserratMedium text-ft15-26.25">
                  {isEdit ? "Update Coupons" : "Add Coupons"}
                </span>
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
AddPartnerCoupon.propTypes = {
  handleClose: PropTypes.func.isRequired,
  item: PropTypes.object, // Optional
  isEdit: PropTypes.bool.isRequired,
  fetchCouponData: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired
};
export default AddPartnerCoupon;
